import React, { useEffect, useRef, useState } from "react";
import "./OrderDetailsPage.scss";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GGButton from "../../components/CommonComponents/GGButton/GGButton";
import ReplayIcon from "@mui/icons-material/Replay";
import SourceIcon from "@mui/icons-material/Source";
import DescriptionIcon from "@mui/icons-material/Description";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { Delivery, Deliverable } from "../../Models/Delivery";
import { useNavigate, useParams } from "react-router-dom";
import { DeliveryAPIHandler } from "../../services/NetworkClient/BackendAPIs/DeliveryAPIHandler";
import Navbar from "../../components/CommonComponents/Navbar/Navbar";
import { Avatar, Box, CircularProgress, Typography } from "@mui/material";
import AddSpace from "../../components/UtilityComponents/AddSpace/AddSpace";
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';
import { OrderAPIHandler } from "../../services/NetworkClient/BackendAPIs/OrderAPIHandler";
import GGLoaderButton from "../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SnackBar, {
  SnackTypes,
} from "../../components/UtilityComponents/SnackBar/SnackBar";
import { Order, OrderStatus } from "../../Models/Order";
import CustomMessageDeliveryHandler from "../../services/ChatManager/CustomMessageDeliveryHandler";
import GGModalWindow from "../../components/UtilityComponents/GGModalWindow/GGModalWindow";
import AskForRevisionModal from "./AskForRevisionModal";
import ImageListModal from "../../components/CommonComponents/ImageListModal/ImageListModal";
import OrderDetailsPageRatingsSection from "../../components/PageComponents/OrderDetailsPageComponents/OrderDetailsPageRatingsSection";
import GGLoaderView from "../../components/UtilityComponents/GGLoaderView/GGLoaderView";
import Footer from "../../components/CommonComponents/Footer/Footer";
import { ErrorLogger } from "../../utils/Logger";

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [delivery, setDelivery] = useState<Delivery | undefined>(undefined);
  const [order, setOrder] = useState<Order | undefined>(undefined);
  const [mainLoader, setMainLoader] = useState(false)
  const [isOrderAccepted, setIsOrderAccepted] = useState<boolean>(true);
  const [orderAcceptanceLoading, setOrderAcceptanceLoading] =
    useState<boolean>(false);
  const [showAskForRevisionModal, setShowAskForRevisionModal] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const [receiptDownloadLoader, setReceiptDownloadLoader] = useState(false);
  const [invoiceDownloadLoader, setInvoiceDownloadLoader] = useState(false);
  
  
  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error,
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state });
    snackBarRef.current?.showSnackbar();
  };

  const fetchDelivery = async (orderId: string) => {
    try {
      setMainLoader(true)
      const { delivery, order } =
        await DeliveryAPIHandler.shared.getBuyerDeliveryByOrderId(
          orderId ?? ""
        );
      setDelivery(delivery);
      setOrder(order);
    } catch (error) {
      ErrorLogger.log(error, 'OrderDetailsPage.fetchDelivery');
    } finally {
      setMainLoader(false)
    }
  };

  const sendOrderApprovedChatMessage = async (order: Order) => {
    const orderCompletionTS = (
      order.orderCompletionTimeStamp ?? new Date()
    ).getTime();
    const channelId: string = order.metaData.channelId;
    const messageDeliveryHandler = new CustomMessageDeliveryHandler(channelId);
    await messageDeliveryHandler.sendOrderCompletedustomMessage(
      order.id,
      orderCompletionTS,
      {
        buyerId: order.metaData.buyer.id,
        sellerId: order.metaData.seller.id,
      }
    );
  };

  const acceptDelivery = async (orderId: string) => {
    setOrderAcceptanceLoading(true);
    try {
      const { delivery, order } = await OrderAPIHandler.shared.approveOrder(
        orderId ?? ""
      );
      await sendOrderApprovedChatMessage(order);
      setDelivery(delivery);
      setOrder(order);
      showSnackbar("Order delivery accepted successfully!", SnackTypes.success);
    } catch (error) {
      showSnackbar(
        (error as string) || "Order delivery acceptance failed!",
        SnackTypes.error
      );
      ErrorLogger.log(error, 'OrderDetailsPage.acceptDelivery');
    } finally {
      setOrderAcceptanceLoading(false);
    }
  };

  useEffect(() => {
    const isOrderInAcceptedState =
      order?.status === OrderStatus.approvedByBuyer ||
      order?.status === OrderStatus.completed;
    setIsOrderAccepted(isOrderInAcceptedState);
  }, [order]);

  useEffect(() => {
    document.title = "GenieGig | Order Details";
    logAnalyticsEvent(GGAnalyticsEvents.BUYER_ORDER_DETAILS_PAGE_LOADED);
    fetchDelivery(orderId ?? "");
    // eslint-disable-next-line
  }, []);

  const OrderDetailsComponent = ({ delivery }: { delivery: Delivery }) => {
    const deliverablesRowsCount = delivery.deliverables.length;
    const overlayHeight = deliverablesRowsCount * 55;

    const lockedOverlayStyle: React.CSSProperties = {
      height: `${overlayHeight}px`,
      gap: "5px",
      ...(deliverablesRowsCount === 1 && {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
      }),
    };

    const handleViewGigBtnClick = (gigId: string) => {
      navigate("/gig/" + gigId);
    };


        const downloadInvoice = async (orderId: string) => {
          try {
            setInvoiceDownloadLoader(true);
    
            // const blob = await OrderAPIHandler.shared.downloadOrderInvoicePdf(orderId);
            const blob = await OrderAPIHandler.shared.generateTaxInvoiceForBuyerAPI(
              orderId
            );
    
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Invoice.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          } catch (err) {
            ErrorLogger.log(err, 'OrderDetailsPage.OrderDetailsComponent.downloadInvoice');
          } finally {
            setInvoiceDownloadLoader(false);
          }
        };
    
        const downloadOrderReceipt = async (orderId: string) => {
          try {
            setReceiptDownloadLoader(true);
    
            const blob = await OrderAPIHandler.shared.downloadOrderReceiptPdfForBuyer(
              orderId
            );
    
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Receipt.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          } catch (err) {
            ErrorLogger.log(err, 'OrderDetailsPage.OrderDetailsComponent.downloadOrderReceipt');
          } finally {
            setReceiptDownloadLoader(false);
          }
        };
    

    return (
      <div className="delivery-note-main-box">
        <div className="main-heading">
          <div className="folder-icon">
            <SourceIcon />
            <p>{isOrderAccepted ? "Order Details" : "Delivery Note"}</p>
          </div>
        </div>
        <div className="delivery-note-container">
          <div className="delivery-note">
            {/* Preview Images */}
            <section className="preview-order">
              <h2>
                {isOrderAccepted
                  ? "Preview Files"
                  : "Preview images of your Order"}
              </h2>
              <br></br>
              <div className="preview-images">
                {delivery.previewScreenshots?.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Preview ${index + 1}`}
                    className="preview-image"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setImageModalIndex(index);
                      setImageModalOpen(true);
                    }}
                  />
                ))}
              </div>
            </section>
            <br></br>
            {/* Original Files */}
            <section className="original-files">
              <h2>Original Files</h2>
              <table className="files-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>File Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th className="locked-overlay-wrapper">
                      {!isOrderAccepted && (
                        <div
                          className="locked-overlay"
                          style={lockedOverlayStyle}
                        >
                          <LockOutlinedIcon
                            className="lock-icon"
                            fontSize={
                              deliverablesRowsCount === 1 ? "medium" : "large"
                            }
                          />
                          <Typography
                            className="locked-overlay-msg"
                            fontSize={
                              deliverablesRowsCount === 1 ? "16px" : "20px"
                            }
                          >
                            Unlocks after order is accepted
                          </Typography>
                        </div>
                      )}
                      Download Link
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {delivery.deliverables.map(
                    (file: Deliverable, index: number) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{file.fileName}</td>
                        <td>{file.fileType}</td>
                        <td>
                          {(file.fileSizeInBytes / (1024 * 1024)).toFixed(2)} MB
                        </td>
                        <td>
                          {isOrderAccepted ? (
                            <a href={file.mediaUrl} className="download-button">
                              <DownloadIcon className="download-icon" />
                              Download
                            </a>
                          ) : (
                            <span className="locked-overlay-1"></span>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </section>

            {/* Remarks */}
            {!isOrderAccepted && (
              <>
                <section className="remarks">
                  <h2>Remarks</h2>
                  <p className="remarks-content">{delivery.remarks}</p>
                </section>
                <section className="action-buttons">
                  <p>
                    By clicking <strong>Accept Order</strong>, you agree to the{" "}
                    <a href="/order-completion-terms">Order Completion Terms</a>
                    .
                  </p>
                  <div className="buttons">
                    <GGLoaderButton
                      className="accept-button"
                      onClick={() => acceptDelivery(orderId ?? "")}
                      isLoading={orderAcceptanceLoading}
                    >
                      <div className="accept-btn-tick">
                        <CheckCircleOutlineIcon />
                      </div>
                      Accept Order
                    </GGLoaderButton>

                    <GGButton
                      className="revision-button"
                      onClick={() => setShowAskForRevisionModal(true)}
                      variant="outlined"
                    >
                      <div className="replay-icon">
                        <ReplayIcon />
                      </div>
                      Ask for Revision{" "}
                      {order?.noOfRevisionsIncluded !== 0
                        ? `(${order?.noOfRevisionsIncluded})`
                        : `(₹${(order?.revisionCharge! / 100).toString()})`}
                    </GGButton>
                  </div>
                </section>
              </>
            )}
          </div>

          {/* Order Completion Details */}
          {isOrderAccepted && (
            <section className="order-complete">
              <div className="order-complete-icon">
                <DescriptionIcon />
                <h2>Order Complete</h2>
              </div>

              <div className="download-invoice-btn-section">
                <GGLoaderButton
                  className="download-invoice-btn bitton"
                  isLoading={invoiceDownloadLoader}
                  onClick={() => downloadInvoice(orderId ?? "")}
                  circularProgressColorHex="#000000"
                >
                  <DownloadIcon className="download-icon" />
                  Download Invoice
                </GGLoaderButton>
                <GGLoaderButton
                  className="download-order-receipt-btn bitton"
                  isLoading={receiptDownloadLoader}
                  onClick={() => downloadOrderReceipt(orderId ?? "")}
                  circularProgressColorHex="#000000"
                >
                  <DownloadIcon className="download-icon" />
                  Download Order Receipt
                </GGLoaderButton>
              </div>

              <div className="order-details">
                <div className="order-info">
                  <div className="head-left">
                    <Typography className="data" fontSize={12}>
                      Order ID: #{order?.id}
                    </Typography>
                    <span>|</span>
                    <Typography className="data" fontSize={12}>
                      Placed on:{" "}
                      {new Date(
                        order?.createdAt ?? Date.now()
                      ).toLocaleDateString()}
                    </Typography>
                  </div>

                  <div className="order-body-left">
                    <Avatar
                      className="icon"
                      alt=""
                      src={order?.metaData.gig.coverImg}
                    />
                    <div className="body">
                      <Typography
                        className="gig-title"
                        fontSize={16}
                        marginBottom={5}
                      >
                        {order?.metaData.gig.title}
                      </Typography>
                      <div className="gig-freelancer">
                        Sold By{" "}
                        <Avatar
                          className="freelancer-icon"
                          alt=""
                          src={order?.metaData.seller.profileImage}
                        />{" "}
                        <a
                          href={"/profile/" + order?.metaData.seller}
                          className="freelancer-name"
                        >
                          {order?.metaData.seller.fullName}
                        </a>
                      </div>
                      <div className="gig-bottom">
                        <GGButton
                          className="btn1"
                          variant="text"
                          disableClickShrink
                          onClick={() => {
                            if (order?.metaData.gig.id) {
                              handleViewGigBtnClick(order.metaData.gig.id);
                            }
                          }}
                        >
                          View Gig
                        </GGButton>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary on the right side */}

                {/* <div className="order-summary">
                  <h3>Order Summary:</h3>
                  <ul>
                    <ul className="order-amount">
                      <li>Gig amount:</li>
                      <li>₹10</li>
                    </ul>
                    <ul className="order-amount">
                      <li>Platform charges (7.5%):</li>
                      <li>₹0.75</li>
                    </ul>
                    <ul className="order-amount">
                      <li>Promo Applied:</li>
                      <li>IWANTMOREDISCOUNT</li>
                    </ul>
                    <ul className="order-amount">
                      <li>Promo Discount:</li>
                      <li>₹2</li>
                    </ul>
                    <ul className="order-amount">
                      <li>Additional revisions (2) @ ₹200 /- revision:</li>
                      <li>₹400</li>
                    </ul>
                    <ul className="order-amount grand-total">
                      <strong>Grand Total:</strong> ₹408.75
                    </ul>
                  </ul>
                  <GGButton className="download-invoice-btn">
                    Download Invoice
                  </GGButton>
                </div> */}
                

              </div>
            </section>
          )}

          {order && isOrderAccepted && (
           <OrderDetailsPageRatingsSection order={order} onSubmitCompletion={() => fetchDelivery(order.id)} />
          )}
          
        </div>
        {order && (
          <GGModalWindow
            open={showAskForRevisionModal}
            onCloseHandler={() => setShowAskForRevisionModal(false)}
            title={`Ask for Revision (${order?.noOfRevisionsIncluded !== 0
              ? order?.noOfRevisionsIncluded
              : "₹" + (order?.revisionCharge! / 100).toString()})`}
            childView={<AskForRevisionModal order={order} />}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <AddSpace vSpace={"7vh"} hSpace={"0"} />
      <Navbar useScrollVersion={true} />
      <SnackBar
        ref={snackBarRef}
        severity={snackBarParams.state}
        alertDuration={5000}
        message={snackBarParams.message}
      />
      {mainLoader && <GGLoaderView /> }

      {delivery ? (
        <OrderDetailsComponent delivery={delivery} />
      ) : (
        <Box className="center" width="100%" height={"80vh"}>
          <CircularProgress
            size={64}
            thickness={5}
            sx={{ marginTop: "75px", color: "grey" }}
          />
        </Box>
      )}
      <Footer />
      <ImageListModal
        images={delivery?.previewScreenshots || []}
        initialIndex={imageModalIndex}
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
      />
    </div>
  );
};

export default OrderDetailsPage;
